import { useDispatch } from 'react-redux';
import { useLiveVideoSetup } from '@rsos/live-video';
import {
  useHasActiveVideoSession,
  useMyVideoSessionRole,
  VideoSessionRole,
} from '@rsos/reduxDataStore';
import { useSelectedConversation } from '@rsos/reduxDataStore/src/features/conversations/utils';
import { useSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import {
  LIVE_VIDEO_CARD,
  setCardsToRender,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { getVideoLink } from '@rsos/utils/fetchVideoServices';
import usePreconfiguredMessages from '../../TF911ChatBox/usePreconfiguredMessages';
import { OnStartConversation } from './OnStartConversation';
import { getIsSelectedChatActive, getSelectedConversationID } from './utils';

interface Props {
  onStartConversation: OnStartConversation;
}

export type VideoSessionInitialization = {
  /**
   * Shows the live video call card. If no video session exists, one is created,
   * and a link is sent to the caller.
   */
  openVideoCall: () => Promise<void>;
  /**
   * Sends a video session link to the caller even if the session already exists.
   */
  sendVideoLink: () => Promise<void>;
  /**
   * True iff there's an active text session and an active video session for
   * this call.
   */
  videoCallStarted: boolean;
  /**
   * Current user's role in the video call; are they the owner?
   */
  role: VideoSessionRole;
};

export function useVideoSessionInitialization({
  onStartConversation,
}: Props): VideoSessionInitialization {
  const dispatch = useDispatch();

  // Call stuff
  const selectedCallerID = useSelectedCallerID();

  if (!selectedCallerID) {
    throw Error('useVideoSessionInitialization hook called outside a call');
  }

  const orgName = useCurrentOrgName();

  // Conversation stuff
  const conversation = useSelectedConversation();

  // tf911 stuff
  const tf911conversation =
    useSelectedTextFrom911Conversation(selectedCallerID);

  // Video stuff
  const { getCallerToken } = useLiveVideoSetup({
    orgName,
    callID: selectedCallerID,
  });

  // Chat stuff
  const conversationID = getSelectedConversationID(
    conversation,
    tf911conversation,
  );
  const isChatActive = getIsSelectedChatActive(conversation, tf911conversation);

  const [conversationStartMessage] = usePreconfiguredMessages();
  const videoCallStarted = useHasActiveVideoSession(selectedCallerID);
  const role = useMyVideoSessionRole(selectedCallerID);

  if (!selectedCallerID) {
    throw Error('useVideoSessionInitialization hook called outside a call');
  }

  const openVideoCall = async () => {
    dispatch(
      setCardsToRender({
        cardName: LIVE_VIDEO_CARD,
        shouldHideHeader: true,
      }),
    );
    if (!isChatActive || !videoCallStarted) {
      await sendVideoLink();
    }
  };

  const startChat = async (message: string) => {
    if (isChatActive) return;

    const { isSuccess, data: conversationData } = await onStartConversation(
      `${conversationStartMessage}. ${message}`,
    );

    const hasConversation = Boolean(
      conversationData?.conversation_id || conversation,
    );

    if (!isSuccess || !hasConversation) {
      throw Error(
        'Failed to start conversation or no conversation ID returned',
      );
    }
  };

  const sendMessage = async (message: string) => {
    if (!isChatActive) {
      return startChat(message);
    }
    const talkjsSession = window.__RSOS?.talkjsSession;
    const currentChat =
      await talkjsSession?.getOrCreateConversation(conversationID);

    currentChat?.sendMessage(message);
  };

  async function sendVideoLink() {
    const token = await getCallerToken();

    const videoURL = getVideoLink(token);

    // TalkJS has been having some issues in dev environments; log the URL so
    // video dev can continue without getting derailed by talkjs debugging.
    // Remove if talkjs starts behving itself again
    //
    // eslint-disable-next-line no-console
    console.log(videoURL);

    await sendMessage(
      `To share live video and allow 911 access to your cameras and microphones, click here: ${videoURL}`,
    );
  }

  return {
    openVideoCall,
    sendVideoLink,
    videoCallStarted: isChatActive && videoCallStarted,
    role,
  };
}
